// A part tilted on its own axes: Rotation X and Y (`rotationX` / `rotationY`,
// degrees, right-hand) after its plan Rotation Z (`rotation`) — R = Rz·Ry·Rx about
// the body's middle; the body then stands with its LOWEST point at its Height from
// ground, its middle over its plan position. Only some kinds tilt (`canTilt`, a
// category's `tilts`: shared/categories.ts); everything else stays upright. Pure: the plan, the sections, the 3D view and the
// edit tools all read it.

import type {
  ProductInstance,
  Vec2,
} from '../shared/types';
import { traitsOf } from '../shared/categories';

export interface Vec3 { x: number; y: number; z: number }
/** A 3×3 matrix, row-major. */
export type Mat3 = readonly [number, number, number, number, number, number, number, number, number];

type Tiltable = Pick<ProductInstance, 'category' | 'autoBenches' | 'nodes' | 'rotation' | 'rotationX' | 'rotationY'>;

/** Whether a part may tilt: of a category that `tilts` (a custom part, a light,
 *  an infrared panel, a fan, a steam conveyor, a shower part, a SpaManager box —
 *  a junction box stands upright on a wall: products.md, *Mounted by where it
 *  is dropped* —, a control unit's part, a steam generator's part: its sensor
 *  stands on a wall), placed whole (not a room, not a node run). */
export function canTilt(inst: Pick<ProductInstance, 'category' | 'autoBenches' | 'nodes'>): boolean {
  return traitsOf(inst.category).tilts && !inst.autoBenches && !(inst.nodes && inst.nodes.length);
}

const rad = (deg: number): number => (deg * Math.PI) / 180;
const flat = (deg: number | undefined): boolean => Math.abs(Math.sin(rad(deg ?? 0))) < 1e-9 && Math.cos(rad(deg ?? 0)) > 0;

/** Whether a part stands tilted (a Rotation X or Y other than none). */
export function isTilted(inst: Tiltable): boolean {
  return canTilt(inst) && !(flat(inst.rotationX) && flat(inst.rotationY));
}

export function rotX(deg: number): Mat3 {
  const c = Math.cos(rad(deg)), s = Math.sin(rad(deg));
  return [1, 0, 0, 0, c, -s, 0, s, c];
}
export function rotY(deg: number): Mat3 {
  const c = Math.cos(rad(deg)), s = Math.sin(rad(deg));
  return [c, 0, s, 0, 1, 0, -s, 0, c];
}
export function rotZ(deg: number): Mat3 {
  const c = Math.cos(rad(deg)), s = Math.sin(rad(deg));
  return [c, -s, 0, s, c, 0, 0, 0, 1];
}
export function mul(a: Mat3, b: Mat3): Mat3 {
  const o: number[] = [];
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) o.push(a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c]);
  return o as unknown as Mat3;
}
export function apply(m: Mat3, v: Vec3): Vec3 {
  return { x: m[0] * v.x + m[1] * v.y + m[2] * v.z, y: m[3] * v.x + m[4] * v.y + m[5] * v.z, z: m[6] * v.x + m[7] * v.y + m[8] * v.z };
}

/** A part's tilt alone (Ry·Rx), none for a part that does not tilt. */
export function tiltMatrix(inst: Tiltable): Mat3 {
  return canTilt(inst) ? mul(rotY(inst.rotationY ?? 0), rotX(inst.rotationX ?? 0)) : [1, 0, 0, 0, 1, 0, 0, 0, 1];
}
/** A part's whole orientation: Rz·Ry·Rx. */
export function partMatrix(inst: Tiltable): Mat3 {
  return mul(rotZ(inst.rotation), tiltMatrix(inst));
}

/** A rotation of `deg` about the unit `axis` (right-hand). */
export function rotAxis(axis: Vec3, deg: number): Mat3 {
  const c = Math.cos(rad(deg)), s = Math.sin(rad(deg)), t = 1 - c, { x, y, z } = axis;
  return [t * x * x + c, t * x * y - s * z, t * x * z + s * y, t * x * y + s * z, t * y * y + c, t * y * z - s * x, t * x * z - s * y, t * y * z + s * x, t * z * z + c];
}

/** Angles in (−180, 180], a hair from 0 read as 0. */
function tidy(deg: number): number {
  let d = ((deg % 360) + 540) % 360 - 180;
  if (d <= -180 + 1e-9) d = 180;
  return Math.abs(d) < 1e-7 ? 0 : d;
}

/** The plan Rotation Z and the Rotation Y and X an orientation is (R = Rz·Ry·Rx);
 *  straight up or down (Y = ±90°) its X is none. */
export function anglesOf(m: Mat3): { rotation: number; rotationX: number; rotationY: number } {
  const sy = -m[6], cy = Math.hypot(m[7], m[8]);
  const y = Math.atan2(sy, cy);
  if (cy < 1e-9) return { rotation: tidy((Math.atan2(-m[1], m[4]) * 180) / Math.PI), rotationX: 0, rotationY: tidy((y * 180) / Math.PI) };
  return {
    rotation: tidy((Math.atan2(m[3], m[0]) * 180) / Math.PI),
    rotationX: tidy((Math.atan2(m[7], m[8]) * 180) / Math.PI),
    rotationY: tidy((y * 180) / Math.PI),
  };
}

/** A body's size: its plan width and depth, and its own height. */
export interface BodySize { w: number; d: number; h: number }

/** Half the tilted body's extents in its plan frame (along its Rotation Z's x and
 *  y), and its whole height: the box a tilted part is selected, snapped and
 *  carved by, and the height it stands. */
export function tiltedExtents(inst: Tiltable, size: BodySize): { hw: number; hd: number; hz: number } {
  const m = tiltMatrix(inst), { w, d, h } = size;
  return {
    hw: (Math.abs(m[0]) * w + Math.abs(m[1]) * d + Math.abs(m[2]) * h) / 2,
    hd: (Math.abs(m[3]) * w + Math.abs(m[4]) * d + Math.abs(m[5]) * h) / 2,
    hz: Math.abs(m[6]) * w + Math.abs(m[7]) * d + Math.abs(m[8]) * h,
  };
}

/** Where a body stands: its plan position, its lowest point's height (`base`),
 *  its size and orientation. */
export interface Body { x: number; y: number; base: number; size: BodySize; m: Mat3 }

export function bodyOf(inst: Tiltable & Pick<ProductInstance, 'x' | 'y'>, base: number, size: BodySize): Body {
  return { x: inst.x, y: inst.y, base, size, m: partMatrix(inst) };
}

/** The body's middle in the world. */
export function bodyCentre(b: Body): Vec3 {
  const hz = Math.abs(b.m[6]) * b.size.w + Math.abs(b.m[7]) * b.size.d + Math.abs(b.m[8]) * b.size.h;
  return { x: b.x, y: b.y, z: b.base + hz / 2 };
}

/** A point of the body's own frame (x across its width and y across its depth
 *  from its middle, z up from its underside) in the world. */
export function bodyPoint(b: Body, p: Vec3): Vec3 {
  const c = bodyCentre(b), r = apply(b.m, { x: p.x, y: p.y, z: p.z - b.size.h / 2 });
  return { x: c.x + r.x, y: c.y + r.y, z: c.z + r.z };
}

const SIGNS: Array<[number, number, number]> = [[-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1], [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]];

/** The body's eight corners in the world: its underside's four (CCW from its
 *  back left, as seen from above untilted), then its top's. */
export function bodyCorners(b: Body): Vec3[] {
  const { w, d, h } = b.size;
  return SIGNS.map(([sx, sy, sz]) => bodyPoint(b, { x: (sx * w) / 2, y: (sy * d) / 2, z: sz < 0 ? 0 : h }));
}

/** The body's six faces: each its four corners (indices into `bodyCorners`, CCW
 *  seen from outside) and its outward normal in the world. */
const FACES: Array<{ k: [number, number, number, number]; n: Vec3 }> = [
  { k: [0, 3, 2, 1], n: { x: 0, y: 0, z: -1 } },
  { k: [4, 5, 6, 7], n: { x: 0, y: 0, z: 1 } },
  { k: [0, 1, 5, 4], n: { x: 0, y: -1, z: 0 } },
  { k: [1, 2, 6, 5], n: { x: 1, y: 0, z: 0 } },
  { k: [2, 3, 7, 6], n: { x: 0, y: 1, z: 0 } },
  { k: [3, 0, 4, 7], n: { x: -1, y: 0, z: 0 } },
];
export function bodyFaces(b: Body): Array<{ pts: Vec3[]; normal: Vec3 }> {
  const c = bodyCorners(b);
  return FACES.map((f) => ({ pts: f.k.map((i) => c[i]), normal: apply(b.m, f.n) }));
}

/** The body as seen from above: the faces facing up (their outlines are its plan
 *  drawing), each a plan ring. */
export function planFaces(b: Body): Vec2[][] {
  return bodyFaces(b).filter((f) => f.normal.z > 1e-9).map((f) => f.pts.map((p) => ({ x: p.x, y: p.y })));
}

/** The convex hull of plan points, CCW. */
export function hull(pts: Vec2[]): Vec2[] {
  const p = pts.slice().sort((a, b) => a.x - b.x || a.y - b.y);
  if (p.length < 3) return p;
  const cross = (o: Vec2, a: Vec2, b: Vec2): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lo: Vec2[] = [], up: Vec2[] = [];
  for (const q of p) { while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], q) <= 1e-9) lo.pop(); lo.push(q); }
  for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (up.length >= 2 && cross(up[up.length - 2], up[up.length - 1], q) <= 1e-9) up.pop(); up.push(q); }
  lo.pop(); up.pop();
  return lo.concat(up);
}

/** The body's outline on the plan: the hull of its corners from above. */
export function planOutline(b: Body): Vec2[] {
  return hull(bodyCorners(b).map((p) => ({ x: p.x, y: p.y })));
}

/** A vertical plane a view cuts along: through `a`, running along `u` (unit,
 *  horizontal), looking toward `look` (unit, horizontal, square to `u`). */
export interface CutPlane { a: Vec2; u: Vec2; look: Vec2 }

/** The body in a section view, in its frame (t along the cut from `a`, h up):
 *  where the plane cuts it (a solid polygon, or null) and what of it lies beyond,
 *  seen (its outline there, or null). */
export function bodyInSection(b: Body, p: CutPlane): { cut: Vec2[] | null; seen: Vec2[] | null } {
  const c = bodyCorners(b);
  const depth = (q: Vec3): number => (q.x - p.a.x) * p.look.x + (q.y - p.a.y) * p.look.y;
  const th = (q: Vec3): Vec2 => ({ x: (q.x - p.a.x) * p.u.x + (q.y - p.a.y) * p.u.y, y: q.z });
  const ds = c.map(depth);
  const EDGES: Array<[number, number]> = [[0, 1], [1, 2], [2, 3], [3, 0], [4, 5], [5, 6], [6, 7], [7, 4], [0, 4], [1, 5], [2, 6], [3, 7]];
  const on: Vec2[] = [];
  for (const [i, j] of EDGES) {
    if ((ds[i] < 0 && ds[j] > 0) || (ds[i] > 0 && ds[j] < 0)) {
      const k = ds[i] / (ds[i] - ds[j]);
      on.push(th({ x: c[i].x + (c[j].x - c[i].x) * k, y: c[i].y + (c[j].y - c[i].y) * k, z: c[i].z + (c[j].z - c[i].z) * k }));
    }
  }
  c.forEach((q, i) => { if (Math.abs(ds[i]) <= 1e-9) on.push(th(q)); });
  const cut = on.length >= 3 ? hull(on) : null;
  const beyond = c.filter((_, i) => ds[i] > 1e-9).map(th);
  const seen = beyond.length ? hull([...beyond, ...on]) : null;
  return { cut: cut && cut.length >= 3 ? cut : null, seen: seen && seen.length >= 3 ? seen : null };
}

/** A body's placement as a part stores it: plan position, lowest point and its
 *  three rotations. */
export interface Placement { x: number; y: number; base: number; rotation: number; rotationX: number; rotationY: number }

/** A tilt never sinks a part into the floor: lifted straight up until its lowest
 *  point is on it — or, for one already below the floor (`was`, its lowest point
 *  before), until it is no lower than it was. */
export function aboveFloor(p: Placement, was: number): Placement {
  const floor = Math.min(0, was);
  return p.base >= floor ? p : { ...p, base: floor };
}

function placed(m: Mat3, centre: Vec3, size: BodySize): Placement {
  const hz = Math.abs(m[6]) * size.w + Math.abs(m[7]) * size.d + Math.abs(m[8]) * size.h;
  return { x: centre.x, y: centre.y, base: centre.z - hz / 2, ...anglesOf(m) };
}

/** The body turned `deg` about the line through `pivot` along the unit `axis`. */
export function turnBody(b: Body, axis: Vec3, deg: number, pivot: Vec3): Placement {
  const r = rotAxis(axis, deg), c = bodyCentre(b);
  const v = apply(r, { x: c.x - pivot.x, y: c.y - pivot.y, z: c.z - pivot.z });
  return placed(mul(r, b.m), { x: pivot.x + v.x, y: pivot.y + v.y, z: pivot.z + v.z }, b.size);
}

/** The body mirrored across the plane through `at` with the unit normal `n`: its
 *  middle reflected, and its orientation the mirror image turned back the right
 *  way round across its width (its back keeps facing what it faced). */
export function mirrorBody(b: Body, at: Vec3, n: Vec3): Placement {
  const ref: Mat3 = [1 - 2 * n.x * n.x, -2 * n.x * n.y, -2 * n.x * n.z, -2 * n.y * n.x, 1 - 2 * n.y * n.y, -2 * n.y * n.z, -2 * n.z * n.x, -2 * n.z * n.y, 1 - 2 * n.z * n.z];
  const c = bodyCentre(b), dist = (c.x - at.x) * n.x + (c.y - at.y) * n.y + (c.z - at.z) * n.z;
  const flipX: Mat3 = [-1, 0, 0, 0, 1, 0, 0, 0, 1];
  return placed(mul(mul(ref, b.m), flipX), { x: c.x - 2 * dist * n.x, y: c.y - 2 * dist * n.y, z: c.z - 2 * dist * n.z }, b.size);
}

/** The body as it would stand turned to `next` (Rotation X / Y / Z), keeping the
 *  body point `local` (its own frame, as `bodyPoint`) where it is — the pivot a
 *  turn about it. */
export function turnKeeping(b: Body, next: { rotation: number; rotationX: number; rotationY: number }, local: Vec3): Placement {
  const m = mul(rotZ(next.rotation), mul(rotY(next.rotationY), rotX(next.rotationX)));
  const keep = bodyPoint(b, local);
  const r = apply(m, { x: local.x, y: local.y, z: local.z - b.size.h / 2 });
  return placed(m, { x: keep.x - r.x, y: keep.y - r.y, z: keep.z - r.z }, b.size);
}

/** How far to shift a body square to a wall (the plan line through `at`, `inward`
 *  its unit normal into the room) so its nearest point just touches it. */
export function touchShift(b: Body, at: Vec2, inward: Vec2): Vec2 {
  const gap = Math.min(...bodyCorners(b).map((q) => (q.x - at.x) * inward.x + (q.y - at.y) * inward.y));
  return { x: -inward.x * gap, y: -inward.y * gap };
}

/** A room's inner wall face on the plan: from `a` to `b`, `inward` its unit
 *  normal into the room. */
export interface WallFace { a: Vec2; b: Vec2; inward: Vec2 }

/** The wall face a body stands against: one running along its width (within
 *  about 25°) that its nearest corner is within `tol` of — or into it (a body
 *  leaning through its face is against it; one wholly behind it is not) —, the
 *  nearest such. */
export function wallAgainst(b: Body, faces: readonly WallFace[], tol = 100): WallFace | null {
  const ax = { x: b.m[0], y: b.m[3] }, al = Math.hypot(ax.x, ax.y);
  const corners = bodyCorners(b);
  let best: WallFace | null = null, bestGap = tol;
  for (const f of faces) {
    const dx = f.b.x - f.a.x, dy = f.b.y - f.a.y, len = Math.hypot(dx, dy);
    if (len < 1e-9 || al < 1e-9 || Math.abs((dx * ax.x + dy * ax.y) / (len * al)) < 0.9) continue;
    const near = corners.filter((q) => { const t = ((q.x - f.a.x) * dx + (q.y - f.a.y) * dy) / len; return t >= -tol && t <= len + tol; });
    if (!near.length) continue;
    const ins = near.map((q) => (q.x - f.a.x) * f.inward.x + (q.y - f.a.y) * f.inward.y);
    if (Math.max(...ins) < 0) continue;
    const gap = Math.max(0, Math.min(...ins));
    if (gap <= bestGap) { bestGap = gap; best = f; }
  }
  return best;
}
